import styled from "styled-components";
import { GRIFFLE_TYPE, GRIFFLE_RADIUS, GRIFFLE_SPACE } from "../griffleTheme";

export const StyledGriffleLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-page-bg);
  font-family: var(--font-body);

  /* ── Top header ───────────────────────────────────────── */
  .griffle-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: ${GRIFFLE_SPACE.headerHeight};
    background: var(--color-header-bg);
    border-bottom: 1px solid var(--color-header-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px 0 0;
    z-index: 100;
    box-shadow: var(--shadow-header);
  }

  /* Left section of header (logo area matches sidebar width) */
  .header-brand {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${GRIFFLE_SPACE.sidebarWidth};
    height: 100%;
    background: var(--color-sidebar-bg);
    flex-shrink: 0;
    text-decoration: none;
    cursor: pointer;
    border: none;
    padding: 0 20px;
    gap: 10px;
  }

  .griffle-logo-text {
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.logoWeight};
    font-size: ${GRIFFLE_TYPE.logoSize};
    color: #e5face;
    -webkit-text-stroke: 1.5px #152b08;
    paint-order: stroke fill;
    text-shadow: 1px 2px 0 rgba(21, 43, 8, 0.5);
    letter-spacing: 2px;
    line-height: 1;
    user-select: none;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    justify-content: flex-end;
  }

  /* School / org selector placeholder */
  .header-org {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${GRIFFLE_TYPE.body};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: var(--color-ink-muted);
    background: var(--color-page-bg);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-button);
    padding: 5px 12px;
    cursor: pointer;
    transition: border-color 0.15s;

    &:hover {
      border-color: var(--color-primary);
    }
  }

  /* User pill */
  .header-user-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px 5px 5px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    cursor: pointer;
    transition: background 0.15s;
    background: transparent;

    &:hover {
      background: var(--color-page-bg);
    }
  }

  .header-avatar {
    width: 30px;
    height: 30px;
    background: var(--color-primary);
    border-radius: ${GRIFFLE_RADIUS.circle};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.weightBold};
    font-size: 13px;
    color: #fff;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .header-username {
    font-size: ${GRIFFLE_TYPE.body};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: var(--color-ink);
    white-space: nowrap;
  }

  .header-chevron {
    color: var(--color-ink-muted);
    font-size: 10px;
  }

  .header-logout-btn {
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-button);
    padding: 5px 14px;
    font-family: var(--font-body);
    font-size: ${GRIFFLE_TYPE.body};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: var(--color-ink-muted);
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background: var(--color-page-bg);
      border-color: var(--color-ink);
      color: var(--color-ink);
    }
  }

  /* ── Body (sidebar + content) ─────────────────────────── */
  .griffle-body {
    display: flex;
    padding-top: ${GRIFFLE_SPACE.headerHeight};
    min-height: 100vh;
  }

  /* ── Sidebar ──────────────────────────────────────────── */
  .griffle-sidebar {
    position: fixed;
    top: ${GRIFFLE_SPACE.headerHeight};
    left: 0;
    width: ${GRIFFLE_SPACE.sidebarWidth};
    height: calc(100vh - ${GRIFFLE_SPACE.headerHeight});
    background: var(--color-sidebar-bg);
    display: flex;
    flex-direction: column;
    padding: 16px 0 24px;
    z-index: 99;
    overflow-y: auto;
    overflow-x: hidden;
  }

  /* Nav section label */
  .sidebar-section-label {
    font-size: ${GRIFFLE_TYPE.xsmall};
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    color: var(--color-sidebar-text);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0 20px;
    margin: 16px 0 6px;
    opacity: 0.6;
  }

  .sidebar-nav {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 0 10px;
  }

  .sidebar-nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: var(--radius-button);
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s;
    color: var(--color-sidebar-text);

    svg {
      flex-shrink: 0;
      color: var(--color-sidebar-text);
      opacity: 0.7;
      transition: opacity 0.15s, color 0.15s;
    }

    .nav-label {
      font-size: ${GRIFFLE_TYPE.body};
      font-weight: ${GRIFFLE_TYPE.weightMd};
      color: var(--color-sidebar-text);
      white-space: nowrap;
      transition: color 0.15s;
    }

    &:hover {
      background: var(--color-sidebar-hover);

      svg {
        opacity: 1;
        color: #fff;
      }

      .nav-label {
        color: #fff;
      }
    }

    &.active {
      background: var(--color-sidebar-active-bg);

      svg {
        opacity: 1;
        color: #fff;
      }

      .nav-label {
        color: var(--color-sidebar-text-active);
        font-weight: ${GRIFFLE_TYPE.weightSemi};
      }
    }
  }

  /* Active indicator bar */
  .sidebar-nav-item.active {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 4px;
      bottom: 4px;
      width: 3px;
      background: var(--color-primary);
      border-radius: 0 2px 2px 0;
    }
  }

  .sidebar-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
    margin: 12px 16px;
  }

  /* ── Submenu group ── */
  .sidebar-nav-group {
    display: flex;
    flex-direction: column;
  }

  .sidebar-nav-group-trigger {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: var(--radius-button);
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s;
    color: var(--color-sidebar-text);
    background: none;
    border: none;
    width: 100%;
    text-align: left;

    svg {
      flex-shrink: 0;
      color: var(--color-sidebar-text);
      opacity: 0.7;
      transition: opacity 0.15s, color 0.15s;
    }

    .nav-label {
      font-size: ${GRIFFLE_TYPE.body};
      font-weight: ${GRIFFLE_TYPE.weightMd};
      color: var(--color-sidebar-text);
      white-space: nowrap;
      flex: 1;
    }

    .nav-chevron {
      font-size: 10px;
      color: var(--color-sidebar-text);
      opacity: 0.5;
      transition: transform 0.2s;
      margin-left: auto;
    }

    &.open .nav-chevron {
      transform: rotate(180deg);
    }

    &:hover,
    &.open {
      background: var(--color-sidebar-hover);

      svg {
        opacity: 1;
        color: #fff;
      }
      .nav-label {
        color: #fff;
      }
      .nav-chevron {
        opacity: 0.8;
        color: #fff;
      }
    }
  }

  .sidebar-submenu {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 2px 0 4px 0;
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.2s ease;

    &.open {
      max-height: 200px;
    }
  }

  .sidebar-submenu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px 8px 42px;
    border-radius: var(--radius-button);
    font-size: 13px;
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: var(--color-sidebar-text);
    text-decoration: none;
    transition: background 0.15s;
    white-space: nowrap;
    opacity: 0.85;

    &:hover {
      background: var(--color-sidebar-hover);
      color: #fff;
      opacity: 1;
    }

    &.active {
      background: var(--color-sidebar-active-bg);
      color: var(--color-sidebar-text-active);
      font-weight: ${GRIFFLE_TYPE.weightSemi};
      opacity: 1;
    }
  }

  .sidebar-footer {
    padding: 0 10px;
    margin-top: auto;
  }

  /* ── Main content area ────────────────────────────────── */
  .griffle-content {
    margin-left: ${GRIFFLE_SPACE.sidebarWidth};
    flex: 1;
    min-height: calc(100vh - ${GRIFFLE_SPACE.headerHeight});
    background: var(--color-page-bg);
    overflow-x: hidden;
  }

  /* ── Tablet (≤1024px) ─────────────────────────────────── */
  @media (max-width: 1024px) {
    .header-brand {
      width: 72px;
      padding: 0;
      justify-content: center;
    }

    .griffle-logo-text {
      display: none;
    }

    .griffle-sidebar {
      width: 72px;
      padding: 12px 0;
    }

    .sidebar-section-label {
      display: none;
    }

    .sidebar-nav {
      padding: 0 6px;
    }

    .sidebar-nav-item {
      justify-content: center;
      padding: 10px;
      gap: 0;

      .nav-label {
        display: none;
      }

      svg {
        width: 22px;
        height: 22px;
        opacity: 0.8;
      }
    }

    .sidebar-nav-group-trigger {
      justify-content: center;
      padding: 10px;
      gap: 0;

      .nav-label,
      .nav-chevron {
        display: none;
      }

      svg {
        width: 22px;
        height: 22px;
        opacity: 0.8;
      }
    }

    /* Never show the submenu flyout on tablet icon-only sidebar */
    .sidebar-submenu {
      display: none !important;
    }

    .sidebar-footer {
      padding: 0 6px;
    }

    .griffle-content {
      margin-left: 72px;
    }
  }

  /* ── Mobile (≤768px) ──────────────────────────────────── */
  @media (max-width: 768px) {
    .header-brand {
      width: 60px;
    }

    .griffle-header {
      padding: 0 16px 0 0;
    }

    .griffle-body {
      padding-bottom: 64px;
      flex-direction: column;
    }

    .griffle-sidebar {
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 64px;
      flex-direction: row;
      align-items: center;
      padding: 0;
      z-index: 200;
      overflow: hidden;
    }

    .sidebar-section-label,
    .sidebar-divider,
    .sidebar-footer {
      display: none;
    }

    .sidebar-nav {
      flex-direction: row;
      flex: 1;
      padding: 0;
      gap: 0;
      justify-content: space-around;
      align-items: center;
      height: 100%;
    }

    .sidebar-nav-item {
      flex-direction: column;
      gap: 3px;
      padding: 6px 8px;
      border-radius: 0;
      flex: 1;
      height: 100%;
      justify-content: center;

      &::before {
        display: none;
      }

      .nav-label {
        display: block;
        font-size: 10px;
      }

      svg {
        width: 20px;
        height: 20px;
      }

      &.active {
        background: none;

        .nav-label {
          color: var(--color-primary);
        }

        svg {
          color: var(--color-primary);
        }
      }
    }

    /* Progress Report group — renders as a flat bottom-bar tab */
    .sidebar-nav-group {
      flex: 1;
      height: 100%;
    }

    .sidebar-nav-group-trigger {
      flex-direction: column;
      gap: 3px;
      padding: 6px 8px;
      border-radius: 0;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;

      .nav-label {
        display: block;
        font-size: 10px;
        flex: none;
      }

      .nav-chevron {
        display: none;
      }

      svg {
        width: 20px;
        height: 20px;
      }

      &.open {
        background: none;
      }

      &:hover,
      &.open {
        .nav-label {
          color: #fff;
        }
      }
    }

    /* Active state when on any progress-report route */
    .sidebar-nav-group-trigger.open {
      .nav-label {
        color: var(--color-primary);
      }

      svg {
        color: var(--color-primary);
      }
    }

    /* Hide the submenu entirely — no room on the bottom bar */
    .sidebar-submenu {
      display: none !important;
    }

    .griffle-content {
      margin-left: 0;
    }
  }
`;
